import React from "react";
import { Link } from "react-router-dom";

const EFFECTIVE_DATE = "15 February 2026";
const CONTROLLER = "Henry Hoe (Freelance Developer, Singapore)";
const CONTACT_EMAIL = "info@leadifycrm.com";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-2" data-testid="privacy-logo-link">
            <div className="h-8 w-8 rounded-sm bg-primary flex items-center justify-center font-black text-black">L</div>
            <div className="font-display font-black text-lg">LeadifyCRM</div>
          </Link>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/privacy" className="text-primary" data-testid="link-privacy">Privacy</Link>
            <Link to="/terms" className="text-muted-foreground hover:text-white" data-testid="link-terms">Terms</Link>
            <Link to="/login" className="text-muted-foreground hover:text-white">Sign in</Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-8" data-testid="privacy-page">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Legal</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black mt-1">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground mt-2">Effective {EFFECTIVE_DATE}</p>
        </div>

        <Section title="1. Who we are">
          <p>
            LeadifyCRM ("the Service", "we", "us") is operated by {CONTROLLER}. This Privacy Policy explains what
            personal data we collect from users of the Service, how we use it, and the rights you have.
          </p>
          <p>
            We act as a <b>data controller</b> for information about the customer's own team members (users of the
            CRM) and as a <b>data processor</b> for information about the customer's end-users (contacts, leads,
            messages routed through the Inbox, etc.) that our customers upload or connect to the Service.
          </p>
        </Section>

        <Section title="2. Data we collect">
          <ul className="space-y-2 list-disc ml-5">
            <li><b>Account information</b> — name, email, hashed password, role, division, avatar.</li>
            <li><b>Contact records</b> — names, email addresses, phone numbers, avatars, tags, notes uploaded or synced by the customer.</li>
            <li><b>Conversation content</b> — messages exchanged over Web chat, Email (IMAP), WhatsApp, Facebook Messenger, and Instagram Direct.</li>
            <li><b>Meeting bookings</b> — attendee name, email, date/time, meeting type, cancellation tokens.</li>
            <li><b>Third-party access tokens</b> — encrypted at rest: IMAP/SMTP passwords (Fernet), Google Calendar OAuth tokens, Facebook Page Access Tokens, WhatsApp Business API credentials.</li>
            <li><b>Usage & telemetry</b> — login timestamps, feature interactions, error logs (no third-party analytics beacons).</li>
            <li><b>Cookies</b> — session cookies for authentication (`HttpOnly`, `Secure`). No advertising cookies.</li>
          </ul>
        </Section>

        <Section title="3. How we use your data">
          <p>Data is used strictly to provide the Service, including:</p>
          <ul className="space-y-1 list-disc ml-5">
            <li>Authenticating users and enforcing role-based access</li>
            <li>Routing messages from connected channels into the Inbox</li>
            <li>Categorising leads via keyword + optional LLM classifier (see §5)</li>
            <li>Sending transactional emails (booking confirmations, task digests, password reset)</li>
            <li>Creating Google Calendar events with Google Meet links when users book meetings</li>
            <li>Diagnosing bugs and preventing abuse</li>
          </ul>
          <p>We do <b>not</b> sell personal data. We do <b>not</b> use it to train shared machine-learning models.</p>
        </Section>

        <Section title="4. Meta Platform data (Facebook Messenger + Instagram)">
          <p>
            When you connect a Facebook Page or Instagram Business account, the Service receives inbound message
            content, sender profile (name + avatar), and message metadata via Meta's official webhooks. This data is
            stored solely to display in the CRM Inbox and is never shared outside the customer's division.
          </p>
          <ul className="space-y-1 list-disc ml-5">
            <li>Message content and sender profile fields are stored in our MongoDB database.</li>
            <li>Page Access Tokens are stored in our database in plaintext form as required by Meta's Graph API.</li>
            <li>You may revoke access at any time from your Facebook Page Settings → Business Integrations, or delete the mapping in Settings → Integrations.</li>
            <li>User Data Deletion Requests can be sent to <a className="text-primary underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We will delete all associated data within 30 days.</li>
          </ul>
        </Section>

        <Section title="5. Third-party subprocessors">
          <p>We rely on a small number of vendors to deliver the Service:</p>
          <ul className="space-y-1 list-disc ml-5">
            <li><b>MongoDB Atlas</b> — primary database (data-at-rest encryption AES-256).</li>
            <li><b>SMTP provider(s)</b> — customer-configured (Gmail, Outlook, or a company SMTP). Outbound email leaves your own mailbox.</li>
            <li><b>LLM providers</b> — you choose OpenAI, Anthropic, Google Gemini, DeepSeek, or your own OpenAI-compatible endpoint (self-hosted). Message excerpts are sent for classification and chatbot replies only when you enable those features.</li>
            <li><b>Google Calendar API</b> — used only for users who click "Connect Google" in Settings.</li>
            <li><b>Meta Graph API</b> — used only for divisions that connect a Page.</li>
          </ul>
          <p>Each subprocessor's own privacy policy governs their handling of the data we forward to them.</p>
        </Section>

        <Section title="6. Data retention">
          <ul className="space-y-1 list-disc ml-5">
            <li>Conversation and lead data are retained for as long as the customer's account is active.</li>
            <li>Deleted records are purged from live database within 7 days and from backups within 30 days.</li>
            <li>Log files are retained for 30 days for security and diagnostics.</li>
            <li>Cancelled/expired OAuth tokens are removed immediately upon disconnection.</li>
          </ul>
        </Section>

        <Section title="7. Your rights (PDPA — Singapore)">
          <p>Under the Personal Data Protection Act 2012 (Singapore), you have the right to:</p>
          <ul className="space-y-1 list-disc ml-5">
            <li>Access the personal data we hold about you</li>
            <li>Correct or update inaccurate personal data</li>
            <li>Withdraw consent for processing (which will terminate access to the Service)</li>
            <li>Request deletion of personal data</li>
            <li>Complain to the Personal Data Protection Commission (PDPC) at <a className="text-primary underline" href="https://www.pdpc.gov.sg" target="_blank" rel="noreferrer">pdpc.gov.sg</a></li>
          </ul>
          <p>To exercise any of these rights, email <a className="text-primary underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We will respond within 30 days.</p>
        </Section>

        <Section title="8. Security">
          <ul className="space-y-1 list-disc ml-5">
            <li>Passwords are hashed with bcrypt.</li>
            <li>IMAP/SMTP passwords are encrypted at rest using Fernet symmetric encryption.</li>
            <li>All traffic is served over TLS 1.2+ / HTTPS.</li>
            <li>Role-based access control isolates data per division and company.</li>
            <li>Webhook signatures (Meta X-Hub-Signature-256, WhatsApp) are validated in production.</li>
          </ul>
        </Section>

        <Section title="9. International transfers">
          <p>
            Our infrastructure is hosted in secure data centres. Some of our subprocessors (Google, Meta, OpenAI,
            Anthropic) may process data outside your country. By using the Service you consent to such transfers,
            subject to adequate contractual protections.
          </p>
        </Section>

        <Section title="10. Children">
          <p>The Service is not directed at children under 13, and we do not knowingly collect their personal data.</p>
        </Section>

        <Section title="11. Changes to this policy">
          <p>
            We may update this Privacy Policy from time to time. Material changes will be announced via in-app
            notice or email at least 14 days before they take effect. The "Effective" date at the top reflects the
            most recent revision.
          </p>
        </Section>

        <Section title="12. Contact">
          <p>
            Questions or requests: <a className="text-primary underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
            <br />Data controller: {CONTROLLER}
          </p>
        </Section>
      </main>

      <footer className="border-t border-border mt-8">
        <div className="max-w-4xl mx-auto px-6 py-6 text-xs text-muted-foreground flex flex-wrap justify-between gap-2">
          <div>© {new Date().getFullYear()} {CONTROLLER}. All rights reserved.</div>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-white">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-3">
      <h2 className="font-display text-xl sm:text-2xl font-extrabold">{title}</h2>
      <div className="text-sm text-muted-foreground space-y-3 leading-relaxed">
        {children}
      </div>
    </section>
  );
}
